import { supabase } from './supabase';
import { recordTimeclockEntry, updateTimeclockEntry } from './timeclock';

export interface PayrollRequest {
  id: string;
  business_id: string;
  resource_id: string;
  payroll_entry_id: string | null;
  request_type: 'change' | 'add' | 'add_flat_rate';
  
  // Original entry data
  original_clock_in?: string;
  original_clock_out?: string;
  original_break_start?: string;
  original_break_end?: string;
  original_total_minutes?: number;
  original_total_earnings?: number;
  original_notes?: string;
  
  // Requested changes
  requested_clock_in?: string;
  requested_clock_out?: string;
  requested_break_start?: string;
  requested_break_end?: string;
  requested_total_minutes?: number;
  requested_total_earnings?: number;
  requested_notes?: string;
  
  // Request metadata
  request_reason?: string;
  status: 'pending' | 'approved' | 'denied';
  requested_at: string;
  
  // For new entry requests
  requested_work_date?: string;
  requested_booking_id?: string;
  reviewed_at?: string;
  reviewed_by?: string;
  reviewer_notes?: string;
  
  // For flat rate requests
  requested_flat_rate_item_id?: string;
  requested_flat_rate_hours?: number;
  requested_flat_rate_completed_at?: string;
  
  created_at: string;
  updated_at: string;
}

export interface CreatePayrollRequestData {
  payroll_entry_id?: string;
  business_id: string;
  resource_id: string;
  request_type: 'change' | 'add' | 'add_flat_rate';
  
  // Original data (for reference - only for change requests)
  original_clock_in?: string;
  original_clock_out?: string;
  original_break_start?: string;
  original_break_end?: string;
  original_total_minutes?: number;
  original_total_earnings?: number;
  original_notes?: string;
  
  // Requested changes/data
  requested_clock_in?: string;
  requested_clock_out?: string;
  requested_break_start?: string;
  requested_break_end?: string;
  requested_notes?: string;
  request_reason?: string;
  
  // For new entry requests
  requested_work_date?: string;
  requested_booking_id?: string;
  
  // For flat rate requests
  requested_flat_rate_item_id?: string;
  requested_flat_rate_hours?: number;
  requested_flat_rate_completed_at?: string; // Inherit completion timestamp from existing cluster
}

// Get the completion timestamp from existing flat rate items for a resource on a booking
export async function getExistingFlatRateCompletionTimestamp(
  bookingId: string, 
  resourceId: string
): Promise<string | null> {
  if (!supabase) throw new Error('Supabase client not initialized');

  console.log('🔍 Searching for existing flat rate completion timestamp...', { bookingId, resourceId });

  // Check both booking_flat_rates AND payroll_page tables since completion might be stored in either
  
  // First check booking_flat_rates table
  const { data: flatRateItems, error: flatRateError } = await supabase
    .from('booking_flat_rates')
    .select('id, completed_at, item_id, hours')
    .eq('booking_id', bookingId)
    .eq('resource_id', resourceId);

  console.log('📋 booking_flat_rates items:', flatRateItems);

  // Also check payroll_page table for flat_rate entries
  const { data: payrollItems, error: payrollError } = await supabase
    .from('payroll_page')
    .select('id, completed_at, flat_rate_id, hours, item_name, entry_type')
    .eq('booking_id', bookingId)
    .eq('resource_id', resourceId)
    .eq('entry_type', 'flat_rate');

  console.log('📋 payroll_page flat_rate entries:', payrollItems);

  // Look for completion timestamp in booking_flat_rates first
  if (flatRateItems && flatRateItems.length > 0) {
    const completedFlatRateItem = flatRateItems.find(item => item.completed_at);
    if (completedFlatRateItem) {
      console.log('✅ Found completion timestamp in booking_flat_rates:', completedFlatRateItem.completed_at);
      return completedFlatRateItem.completed_at;
    }
  }

  // If not found in booking_flat_rates, check payroll_page
  if (payrollItems && payrollItems.length > 0) {
    const completedPayrollItem = payrollItems.find(item => item.completed_at);
    if (completedPayrollItem) {
      console.log('✅ Found completion timestamp in payroll_page:', completedPayrollItem.completed_at);
      return completedPayrollItem.completed_at;
    }
  }

  console.log('❌ No existing completed flat rate items found in either table');
  return null;
}

export async function createPayrollRequest(data: CreatePayrollRequestData): Promise<PayrollRequest> {
  if (!supabase) throw new Error('Supabase client not initialized');

  console.log('Creating payroll request:', data);

  // Calculate requested totals
  let requestedTotalMinutes = 0;
  let requestedTotalEarnings = 0;

  if (data.requested_clock_in && data.requested_clock_out) {
    let totalMs = new Date(data.requested_clock_out).getTime() - new Date(data.requested_clock_in).getTime();
    
    // Subtract break time if applicable
    if (data.requested_break_start && data.requested_break_end) {
      const breakMs = new Date(data.requested_break_end).getTime() - new Date(data.requested_break_start).getTime();
      totalMs -= breakMs;
    }
    
    requestedTotalMinutes = Math.max(0, Math.floor(totalMs / (1000 * 60)));
    
    // Get hourly rate for calculations
    let hourlyRate = 0;
    
    if (data.request_type === 'change' && data.payroll_entry_id) {
      // For change requests, get rate from existing entry
      const { data: entryData, error: entryError } = await supabase
        .from('payroll_page')
        .select(`
          rate_amount,
          resource:resources(hourly_rate)
        `)
        .eq('id', data.payroll_entry_id)
        .single();

      if (!entryError && entryData) {
        hourlyRate = entryData.rate_amount || entryData.resource?.hourly_rate || 0;
      }
    } else if (data.request_type === 'add') {
      // For new entry requests, get rate from resource
      const { data: resourceData, error: resourceError } = await supabase
        .from('resources')
        .select('hourly_rate')
        .eq('id', data.resource_id)
        .single();

      if (!resourceError && resourceData) {
        hourlyRate = resourceData.hourly_rate || 0;
      }
    }
    
    requestedTotalEarnings = (requestedTotalMinutes / 60) * hourlyRate;
  }

  const requestData = {
    ...data,
    requested_total_minutes: requestedTotalMinutes,
    requested_total_earnings: requestedTotalEarnings
  };

  const { data: result, error } = await supabase
    .from('payroll_request')
    .insert(requestData)
    .select()
    .single();

  if (error) {
    console.error('Error creating payroll request:', error);
    throw error;
  }

  console.log('Payroll request created successfully:', result);
  return result;
}

export async function getPayrollRequestsForResource(
  resourceId: string,
  businessId: string
): Promise<PayrollRequest[]> {
  if (!supabase) throw new Error('Supabase client not initialized');

  const { data, error } = await supabase
    .from('payroll_request')
    .select(`
      *,
      payroll_entry:payroll_page(
        id,
        clock_in,
        clock_out,
        total_minutes,
        total_earnings
      )
    `)
    .eq('resource_id', resourceId)
    .eq('business_id', businessId)
    .order('requested_at', { ascending: false });

  if (error) {
    console.error('Error fetching payroll requests:', error);
    throw error;
  }

  return data || [];
}

export async function getPayrollRequestsForBusiness(
  businessId: string, 
  status?: 'pending' | 'approved' | 'denied'
): Promise<PayrollRequest[]> {
  if (!supabase) throw new Error('Supabase client not initialized');

  let query = supabase
    .from('payroll_request')
    .select(`
      *,
      resource:resources(
        id,
        first_name,
        last_name,
        email
      ),
      payroll_entry:payroll_page(
        id,
        clock_in,
        clock_out,
        total_minutes,
        total_earnings
      )
    `)
    .eq('business_id', businessId);

  // Add status filter if provided
  if (status) {
    query = query.eq('status', status);
  }

  const { data, error } = await query.order('requested_at', { ascending: false });

  if (error) {
    console.error('Error fetching business payroll requests:', error);
    throw error;
  }

  // Manually fetch related booking and flat rate item data for requests that need it
  const requests = data || [];
  
  for (const request of requests) {
    // Fetch booking details for requests with booking_id
    if (request.requested_booking_id) {
      const { data: bookingData } = await supabase
        .from('bookings')
        .select('id, booking_number, title')
        .eq('id', request.requested_booking_id)
        .single();
      
      if (bookingData) {
        request.booking = bookingData;
      }
    }
    
    // Fetch flat rate item details for flat rate requests
    if (request.requested_flat_rate_item_id) {
      const { data: itemData } = await supabase
        .from('flat_rate_items')
        .select('id, name, description')
        .eq('id', request.requested_flat_rate_item_id)
        .single();
      
      if (itemData) {
        request.flat_rate_item = itemData;
      }
    }
  }

  return requests;
}

export async function updatePayrollRequestStatus(
  requestId: string,
  status: 'approved' | 'denied',
  reviewerNotes?: string
): Promise<PayrollRequest> {
  if (!supabase) throw new Error('Supabase client not initialized');

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const updateData: any = {
    status,
    reviewed_at: new Date().toISOString(),
    reviewed_by: user.id
  };

  if (reviewerNotes) {
    updateData.reviewer_notes = reviewerNotes;
  }

  const { data, error } = await supabase
    .from('payroll_request')
    .update(updateData)
    .eq('id', requestId)
    .select()
    .single();

  if (error) {
    console.error('Error updating payroll request status:', error);
    throw error;
  }

  return data;
}

/**
 * Approve a payroll request AND apply what it asked for (the status flip alone
 * is not enough): flat-rate approvals insert booking_flat_rates + a payroll
 * entry, "add" creates the timeclock entry, "change" updates the existing one.
 * Shared by the Payroll requests UI and the AI assistant.
 */
export type ApprovablePayrollRequest = Pick<
  PayrollRequest,
  'id' | 'request_type' | 'resource_id' | 'payroll_entry_id'
> &
  Partial<PayrollRequest>;

export async function approvePayrollRequest(request: ApprovablePayrollRequest): Promise<void> {
  if (!supabase) throw new Error('Supabase client not initialized');

  await updatePayrollRequestStatus(request.id, 'approved');

  if (request.request_type === 'add_flat_rate') {
    if (!request.requested_flat_rate_item_id || !request.requested_booking_id) {
      throw new Error('Missing flat rate item or booking ID');
    }

    // Date the entry to when the JOB happened (the booking's day), not "today"
    // (the day it was approved). Falls back to now only if no booking date exists.
    const { data: wbk } = await supabase
      .from('bookings')
      .select('start_date, end_date')
      .eq('id', request.requested_booking_id)
      .single();
    const bookingDay = wbk?.end_date || wbk?.start_date;
    const flatRateWorkTs =
      request.requested_flat_rate_completed_at ||
      (bookingDay ? `${bookingDay}T12:00:00` : new Date().toISOString());

    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) throw new Error('User not authenticated');

    const { data: flatRateEntry, error: flatRateError } = await supabase
      .from('booking_flat_rates')
      .insert({
        booking_id: request.requested_booking_id,
        item_id: request.requested_flat_rate_item_id,
        resource_id: request.resource_id,
        hours: request.requested_flat_rate_hours,
        completed_at: flatRateWorkTs,
        completed_by: user.id, // manager approving, not the resource
        notes: request.request_reason || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .select()
      .single();
    if (flatRateError) throw flatRateError;

    const { data: detailsData, error: detailsError } = await supabase
      .from('booking_flat_rates')
      .select(`
        *,
        bookings!inner (business_id, booking_number, title),
        flat_rate_items!inner (name, description),
        resources!inner (compensation_type)
      `)
      .eq('id', flatRateEntry.id)
      .single();
    if (detailsError) throw detailsError;

    const { error: payrollError } = await supabase
      .from('payroll_page')
      .insert({
        business_id: detailsData.bookings.business_id,
        resource_id: request.resource_id,
        booking_id: request.requested_booking_id,
        flat_rate_id: request.requested_flat_rate_item_id,
        hours: request.requested_flat_rate_hours,
        entry_type: 'flat_rate',
        compensation_type: detailsData.resources.compensation_type,
        process_status: 'UNPAID',
        item_name: detailsData.flat_rate_items.name,
        item_description: detailsData.flat_rate_items.description,
        booking_number: detailsData.bookings.booking_number,
        booking_title: detailsData.bookings.title,
        completed_at: flatRateWorkTs,
        created_at: flatRateWorkTs,
      });
    if (payrollError) throw payrollError;
    return;
  }

  if (request.request_type === 'add') {
    const { data: resource, error: resourceError } = await supabase
      .from('resources')
      .select('business_id')
      .eq('id', request.resource_id)
      .single();
    if (resourceError || !resource) throw new Error('Could not fetch resource details');
    if (!request.requested_clock_in) throw new Error('This request has no clock-in time.');

    // Same engine as a live clock-in: rate snapshot, minutes and totals are
    // derived from the requested times rather than trusted from the request.
    await recordTimeclockEntry(resource.business_id, request.resource_id, {
      clockIn: request.requested_clock_in,
      clockOut: request.requested_clock_out ?? null,
      breakStart: request.requested_break_start ?? null,
      breakEnd: request.requested_break_end ?? null,
      notes: request.requested_notes ?? null,
      source: 'request',
    });
    return;
  }

  if (request.request_type === 'change') {
    if (!request.payroll_entry_id) throw new Error('Missing payroll entry ID for change request');
    if (!request.requested_clock_in) throw new Error('This request has no clock-in time.');
    // The engine recomputes minutes and overtime from the approved times; the
    // request's own totals are only an estimate. A paid shift gets an adjustment.
    await updateTimeclockEntry(request.payroll_entry_id, {
      clockIn: request.requested_clock_in,
      clockOut: request.requested_clock_out ?? null,
      breakStart: request.requested_break_start ?? null,
      breakEnd: request.requested_break_end ?? null,
      notes: request.requested_notes ?? null,
      allowPaid: true,
      reason: 'Approved time change request',
    });
    return;
  }

  throw new Error(`Unknown request type: ${request.request_type}`);
}

// Check if a payroll entry already exists for a resource on a specific date
export async function checkExistingPayrollEntry(
  resourceId: string,
  businessId: string,
  workDate: string // YYYY-MM-DD format
): Promise<boolean> {
  if (!supabase) throw new Error('Supabase client not initialized');

  console.log('Checking for existing payroll entry:', { resourceId, businessId, workDate });

  const startOfDay = `${workDate}T00:00:00.000Z`;
  const endOfDay = `${workDate}T23:59:59.999Z`;

  const { data, error } = await supabase
    .from('payroll_page')
    .select('id')
    .eq('resource_id', resourceId)
    .eq('business_id', businessId)
    .eq('entry_type', 'timeclock')
    .gte('clock_in', startOfDay)
    .lte('clock_in', endOfDay)
    .limit(1);

  if (error) {
    console.error('Error checking existing payroll entry:', error);
    throw error;
  }

  const exists = data && data.length > 0;
  console.log('Existing entry check result:', { exists, count: data?.length || 0 });
  
  return exists;
}

export async function getPendingPayrollRequestsCount(businessId: string): Promise<number> {
  if (!supabase) throw new Error('Supabase client not initialized');

  const { count, error } = await supabase
    .from('payroll_request')
    .select('*', { count: 'exact', head: true })
    .eq('business_id', businessId)
    .eq('status', 'pending');

  if (error) {
    console.error('Error fetching pending payroll requests count:', error);
    return 0;
  }

  return count || 0;
}